import { apiClient } from './apiClient';

// ============================================================================
// Types
// ============================================================================

export interface BooksellerDto {
  id: string;
  name: string;
  slug: string;
  logoUrl?: string;
  isActive: boolean;
  priority: number;
  description?: string;
}

export interface BookDeepLinkDto {
  bookId: string;
  title: string;
  author: string;
  isbn: string;
  coverImageUrl?: string;
  hasLicense: boolean;
  booksellerId: string;
  booksellerName: string;
  booksellerBookId: string;
  format: number; // BookFormat enum: EPUB = 1, PDF = 2
}

export interface BooksellerAuthTestResultDto {
  success: boolean;
  metadataRetrieved?: boolean;
  details?: string;
  errorMessage?: string;
  supportedScopes?: string[];
}

export interface EntitlementApiTestResultDto {
  success: boolean;
  responseTimeMs: number;
  entitlementsCount?: number;
  sampleIsbn?: string[];
  statusCode?: number;
  errorMessage?: string;
}

export interface AuthenticationDiscoveryDto {
  email: string;
  hasLocalAccount: boolean; // True if user exists in IdentityUser table
  booksellers: BooksellerDto[]; // Booksellers user has sessions with
  noAccountsFound: boolean; // True if neither local nor bookseller accounts exist
}

// ============================================================================
// Bookseller Discovery & Configuration
// ============================================================================

/**
 * Get all active booksellers
 * Used to display bookseller options on login page
 */
export async function getActiveBooksellers(): Promise<BooksellerDto[]> {
  const response = await apiClient.get('/api/app/booksellers/active');
  return response.data;
}

/**
 * Get bookseller by slug (used for deep linking)
 * Example: slug = "vitalsource" → /book/vitalsource/123
 */
export async function getBooksellerBySlug(slug: string): Promise<BooksellerDto> {
  const response = await apiClient.get(`/api/app/booksellers/by-slug/${slug}`);
  return response.data;
}

/**
 * Discover booksellers associated with a user's email
 * Returns list of booksellers the user has previously authenticated with
 * Used when user enters email on login page
 */
export async function discoverBooksellersByEmail(email: string): Promise<BooksellerDto[]> {
  const response = await apiClient.get('/api/app/booksellers/discover', {
    params: { email }
  });
  return response.data;
}

/**
 * Discover all authentication methods for a user's email
 * Returns both local account status and associated booksellers
 *
 * Scenarios:
 * 1. Local account only → hasLocalAccount=true, booksellers=[]
 * 2. Bookseller account(s) only → hasLocalAccount=false, booksellers=[...]
 * 3. Both local + bookseller(s) → hasLocalAccount=true, booksellers=[...]
 * 4. No accounts found → hasLocalAccount=false, booksellers=[], noAccountsFound=true
 */
export async function discoverAuthenticationMethods(email: string): Promise<AuthenticationDiscoveryDto> {
  const response = await apiClient.get('/api/app/booksellers/discover-auth-methods', {
    params: { email }
  });
  return response.data;
}

// ============================================================================
// Deep Linking
// ============================================================================

/**
 * Get book by bookseller's external book ID (deep linking)
 * Used when user clicks "Read in LearnLeaf" link on bookseller site
 * Returns book information and checks if current user has license
 */
export async function getBookByBooksellerBookId(
  booksellerBookId: string,
  booksellerId: string
): Promise<BookDeepLinkDto> {
  const response = await apiClient.get('/api/app/books-extn/by-bookseller-book-id', {
    params: { booksellerBookId, booksellerId }
  });
  return response.data;
}

// ============================================================================
// Admin Testing (Requires ManageBooksellers permission)
// ============================================================================

/**
 * Test bookseller authentication configuration
 * Admin tool to verify OIDC setup
 */
export async function testAuthConfiguration(booksellerId: string): Promise<BooksellerAuthTestResultDto> {
  const response = await apiClient.post(`/api/app/bookseller-extn/test-auth-configuration/${booksellerId}`);
  return response.data;
}

/**
 * Test bookseller entitlement API configuration
 * Admin tool to verify API connectivity
 */
export async function testEntitlementApi(
  booksellerId: string,
  testEmail: string
): Promise<EntitlementApiTestResultDto> {
  const response = await apiClient.post(`/api/app/bookseller-extn/test-entitlement-api/${booksellerId}`, {
    testEmail
  });
  return response.data;
}

